import React, { useMemo, useState } from 'react';
import { Row, Col, Button, Spinner, InputGroup, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faSearch } from '@fortawesome/free-solid-svg-icons';
import AlertInstanceCard from './AlertInstanceCard';
import AlertsFilterBar from './AltersFilterBar';
import { useAlertInstances } from '../hooks/useAlertInstances';
import '../styles/IotMeterAlerts.css';

const IotMeterAlerts = ({ meters, sources, currentFinancialYear, financialYearOptions }) => {
  const [filters, setFilters] = useState({
    financialYear: currentFinancialYear,
    sourceIds: [],
    subLocationIds: [],
    modules: [],
    categories: [],
    subCategories: [],
    kpis: [],
    meterIds: [],
    status: null // null = all, or specific status
  });

  const [searchTerm, setSearchTerm] = useState('');

  // Build query parameters for API
  const queryParams = useMemo(() => {
    const params = {};
    
    if (filters.meterIds.length > 0) {
      // If specific meters selected, use them
      params.meterId = filters.meterIds.join(',');
    }
    
    if (filters.status) {
      params.status = filters.status;
    }
    
    return params;
  }, [filters]);

  const { data: alertInstances = [], isLoading, error, refetch } = useAlertInstances(queryParams);

  // Client-side filtering based on meter criteria
  const filteredAlertInstances = useMemo(() => {
    let result = alertInstances;

    // Get meter IDs that match the criteria
    let validMeterIds = new Set();
    
    let filteredMeters = meters;

    // Filter by location
    if (filters.sourceIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => filters.sourceIds.includes(m.sourceId));
    }

    // Filter by sub-location
    if (filters.subLocationIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => 
        filters.subLocationIds.includes(m.subLocationId)
      );
    }

    // Filter by module
    if (filters.modules.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(
          fykpi => fykpi.financialYear === filters.financialYear
        )?.kpi;
        return filters.modules.includes(kpi?.module);
      });
    }

    // Filter by categories
    if (filters.categories.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(
          fykpi => fykpi.financialYear === filters.financialYear
        )?.kpi;
        return filters.categories.includes(kpi?.category);
      });
    }

    // Filter by sub-categories
    if (filters.subCategories.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(
          fykpi => fykpi.financialYear === filters.financialYear
        )?.kpi;
        return filters.subCategories.includes(kpi?.subCategory);
      });
    }

    // Filter by KPIs
    if (filters.kpis.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(
          fykpi => fykpi.financialYear === filters.financialYear
        )?.kpi;
        return filters.kpis.includes(kpi?.kpi);
      });
    }

    // If meterIds filter is set, use it
    if (filters.meterIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => filters.meterIds.includes(m.meterId));
    }

    validMeterIds = new Set(filteredMeters.map(m => m.meterId));

    // Filter alert instances by valid meter IDs
    result = result.filter(alert => validMeterIds.has(alert.meterId));

    // Search by meter ID or alert rule ID
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(alert => 
        alert.meterId.toLowerCase().includes(searchLower) ||
        alert.alertRuleId.toString().includes(searchLower)
      );
    }

    if (filters.status !== null) {
      result = result.filter(alert => alert.status === filters.status);
    }

    return result;
  }, [alertInstances, meters, filters, searchTerm]);

  // Group alerts by escalation level
  const alertsByLevel = useMemo(() => {
    const groups = {
      1: [],
      2: [],
      3: [],
      0: [], // Level 0 or -1 (not escalated yet)
    };

    filteredAlertInstances.forEach(alert => {
      const level = alert.lastEscalatedLevel >= 1 ? alert.lastEscalatedLevel : 0;
      if (groups[level]) {
        groups[level].push(alert);
      }
    });

    return groups;
  }, [filteredAlertInstances]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      financialYear: currentFinancialYear,
      sourceIds: [],
      subLocationIds: [],
      modules: [],
      categories: [],
      subCategories: [],
      kpis: [],
      meterIds: [],
      status: null
    });
    setSearchTerm('');
  };

  return (
    <div className="alerts-tab-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Alert Instances</h2>
          <p className="text-muted mb-0">Monitor and manage active alerts</p>
        </div>
        <Button variant="outline-primary" onClick={() => refetch()}>
          <FontAwesomeIcon icon={faSync} className="me-2" />
          Refresh
        </Button>
      </div>

      {/* Search Bar */}
      <div className="mb-3">
        <InputGroup>
          <InputGroup.Text>
            <FontAwesomeIcon icon={faSearch} />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search by meter ID or alert rule ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
      </div>

      {/* Filters */}
      <AlertsFilterBar
        meters={meters}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
        sources={sources}
        financialYearOptions={financialYearOptions}
        currentFinancialYear={currentFinancialYear}
      />

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger" role="alert">
          Failed to load alert instances. Please try again.
          <Button
            variant="outline-danger"
            size="sm"
            className="ms-3"
            onClick={() => refetch()}
          >
            <FontAwesomeIcon icon={faSync} className="me-1" /> Retry
          </Button>
        </div>
      )}

      {/* Alert Instances List */}
      <div className="alerts-list">
        {isLoading ? (
          <div className="text-center p-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading alert instances...</p>
          </div>
        ) : filteredAlertInstances.length > 0 ? (
          <>
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <div className="text-muted">
                Showing {filteredAlertInstances.length} of {alertInstances.length} alert instances
              </div>
              <div className="alert-legend d-flex gap-3">
                <div className="legend-item">
                  <span className="legend-badge badge-level-0"></span>
                  <span className="ms-2">Not Escalated</span>
                </div>
                <div className="legend-item">
                  <span className="legend-badge badge-level-1"></span>
                  <span className="ms-2">Level 1 (Low)</span>
                </div>
                <div className="legend-item">
                  <span className="legend-badge badge-level-2"></span>
                  <span className="ms-2">Level 2 (Medium)</span>
                </div>
                <div className="legend-item">
                  <span className="legend-badge badge-level-3"></span>
                  <span className="ms-2">Level 3 (Severe)</span>
                </div>
              </div>
            </div>

            <div className="alerts-container">
              {/* Level 3 - Severe */}
              {alertsByLevel[3].length > 0 && (
                <div className="mb-4">
                  <h5 className="text-danger mb-3">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Severe Alerts ({alertsByLevel[3].length})
                  </h5>
                  {alertsByLevel[3].map(alert => (
                    <AlertInstanceCard
                      key={alert.id}
                      alert={alert}
                      meter={meters.find(m => m.meterId === alert.meterId)}
                    />
                  ))}
                </div>
              )}

              {/* Level 2 - Medium */}
              {alertsByLevel[2].length > 0 && (
                <div className="mb-4">
                  <h5 className="text-warning mb-3">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    Medium Priority Alerts ({alertsByLevel[2].length})
                  </h5>
                  {alertsByLevel[2].map(alert => (
                    <AlertInstanceCard
                      key={alert.id}
                      alert={alert}
                      meter={meters.find(m => m.meterId === alert.meterId)}
                    />
                  ))}
                </div>
              )}

              {/* Level 1 - Low */}
              {alertsByLevel[1].length > 0 && (
                <div className="mb-4">
                  <h5 className="text-info mb-3">
                    <i className="fas fa-info-circle me-2"></i>
                    Low Priority Alerts ({alertsByLevel[1].length})
                  </h5>
                  {alertsByLevel[1].map(alert => (
                    <AlertInstanceCard
                      key={alert.id}
                      alert={alert}
                      meter={meters.find(m => m.meterId === alert.meterId)}
                    />
                  ))}
                </div>
              )}

              {/* Level 0 - Not Escalated */}
              {alertsByLevel[0].length > 0 && (
                <div className="mb-4">
                  <h5 className="text-secondary mb-3">
                    <i className="fas fa-clock me-2"></i>
                    Not Yet Escalated ({alertsByLevel[0].length})
                  </h5>
                  {alertsByLevel[0].map(alert => (
                    <AlertInstanceCard
                      key={alert.id}
                      alert={alert}
                      meter={meters.find(m => m.meterId === alert.meterId)}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center p-5 empty-state">
            <h4>No alert instances found</h4>
            <p>
              {alertInstances.length === 0
                ? 'There are no active alerts at this time.'
                : 'Try adjusting your search or filters.'}
            </p>
            {alertInstances.length > 0 && (
              <Button
                variant="outline-primary"
                onClick={handleClearFilters}
                className="mt-2"
              >
                <FontAwesomeIcon icon={faSync} className="me-2" />
                Clear Filters
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IotMeterAlerts;
